/**
 * 보고서 발송 문구 — 회차마다 고객에게 카톡으로 보낼 **보고서 이미지 + 안내문**을 운영자가 한 화면에서 만든다
 * (261020-report-message).
 *
 * ★ **운영자 전용이다.** 실측(계수 전 값)과 온보딩이 끝나는 날을 싣는다 — 둘 다 고객 응답에 없는 값이다(`toQueryView`).
 * ★ 대상은 **그 회차에 우리가 작업한 브랜드**뿐이다 — 진행 내역(보고서 이미지의 첫 섹션)이 하나라도 있는 곳.
 *   나머지는 `skipped` 에 이름만 싣는다(왜 빠졌는지 화면이 보인다).
 * ★ 숫자는 보고서 이미지와 **같은 값**이다 — `now` 는 이미지의 큰 숫자(`headlineOf`), `start` 는 그 질문의 첫 측정일 값.
 *   이미지와 문구가 다른 숫자를 말하면 고객이 어느 쪽을 믿을지 묻게 된다.
 */
import { z } from 'zod';
import { BrandKindQuery, IsoDateTime, KstDate } from '../common.ts';
import { defineRoute } from '../route.ts';
import { Price } from './billing.ts';
import { Cycle } from './works.ts';

export const ReportMessageQuery = z.object({
  text: z.string(),
  /** 그 질문의 **첫 측정일**(전 기간) 그날 표시값 — 표본이 없으면 null */
  start: z.object({ rate: z.number().int(), on: KstDate }).nullable(),
  /** 보고서 이미지의 큰 숫자와 같은 값·같은 설명(`최근 7일(10/13~10/19) 평균` 등) */
  now: z.object({ rate: z.number().int(), caption: z.string() }).nullable(),
  /** `now` 와 **같은 창**의 실측(계수 전) — 온보딩이 끝나면 표시값이 이 값에 닿는다 */
  actual: z.number().int().nullable(),
  /** 표시값이 실측과 같아지는 날(질문 등록일 + 온보딩 기간). 온보딩을 끈 질문·이미 끝난 질문은 null */
  onboardingEndsOn: KstDate.nullable(),
  /** 온보딩 기간(일) — 문구의 「측정 N개월 차」 */
  onboardingDays: z.number().int().nullable(),
});
export type ReportMessageQuery = z.infer<typeof ReportMessageQuery>;

export const ReportMessageWork = z.object({
  label: z.string(),
  n: z.number().int(),
  unit: z.string(),
  /** 숏폼 채널 이름들 — 없으면 null */
  detail: z.string().nullable(),
});

/**
 * 문구 속 결제 링크 자리 — 화면이 결제 링크를 만들어 이 자리를 주소로 바꾼다(261020-pay-link).
 * 주소는 발급 응답에만 한 번 나가므로 서버가 문구에 미리 넣을 수 없다
 */
export const PAY_LINK_SLOT = '[결제 링크]';

/** 안내할 구독 — 해지되지 않은 것 중 금액이 가장 큰 것(무료 + 유료 제안이면 유료) */
export const ReportMessageSubscription = z.object({
  id: z.uuid(),
  name: z.string(),
  status: z.enum(['offered', 'active', 'past_due', 'canceled']),
  price: Price,
  startOn: KstDate.nullable(),
  anchorDay: z.number().int().nullable(),
  nextChargeOn: KstDate.nullable(),
  /** 결제 카드가 붙어 있다 — 붙어 있으면 「등록하신 카드로 자동 결제」 */
  hasCard: z.boolean(),
  /** 이미 열린 결제 링크 — 새로 만들면 이 링크는 죽는다(같은 구독에 열린 링크는 하나) */
  openPayLink: z.object({ userName: z.string(), expiresAt: IsoDateTime }).nullable(),
});
export type ReportMessageSubscription = z.infer<typeof ReportMessageSubscription>;

export const ReportMessageBrand = z.object({
  brandId: z.uuid(),
  brandName: z.string(),
  brandStatus: z.string(),
  /** 그 브랜드의 회차 — 구독일이 브랜드마다 다를 수 있다 */
  cycle: Cycle,
  /** 그 회차의 번호(0 = 오늘이 든 회차) — 보고서 이미지 주소(`?cycle=`)에 그대로 쓴다 */
  back: z.number().int().min(0),
  works: z.array(ReportMessageWork),
  queries: z.array(ReportMessageQuery),
  subscription: ReportMessageSubscription.nullable(),
  /** 서버가 조립한 기본 문구 — 화면에서 고쳐 복사한다 */
  message: z.string(),
});
export type ReportMessageBrand = z.infer<typeof ReportMessageBrand>;

export const listReportMessages = defineRoute({
  method: 'get',
  path: '/api/admin/report-messages',
  access: 'admin',
  summary: '보고서 발송 문구 — 그 회차에 작업한 브랜드마다 진행 내역·언급률(처음 → 지금 → 온보딩 뒤 실측)·결제 안내',
  query: BrandKindQuery.extend({
    /** **브랜드마다 어제가 든 회차**에서 몇 칸 앞인가 — 0(기본)이 그 회차, 1 이 그 앞.
     *  구독일이 브랜드마다 달라 오늘 기준 번호로는 한 화면에서 고를 수 없다(261020-report-cycle-yesterday-basis) */
    cycle: z.coerce.number().int().min(0).max(36).optional(),
  }),
  response: z.object({
    brands: z.array(ReportMessageBrand),
    /** 그 회차에 진행 내역이 없어 빠진 브랜드 */
    skipped: z.array(z.object({ brandId: z.uuid(), brandName: z.string() })),
  }),
});
